'use client';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { PopoverPortal } from '@radix-ui/react-popover';
import { CommandLoading } from 'cmdk';
import { Check, ChevronDown, Loader, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import CustomLoading from '../custom-loading';

export type ComboboxOption<T = any> = {
    value: string | number | any;
    label: string | React.ReactNode;

    disabled?: boolean;
    _origin?: T;
    /**Trường để nhận biết dư liệu đc tạo ra từ nhập tay */
    _isInput?: boolean;
};
export type ComboboxProps<T extends {} = any> = {
    options?: ComboboxOption[];
    value?: string;
    onChange?: (value: string) => void;
    /** */
    onAfterValueChange?: (
        value: string,
        option?: ComboboxOption<T>
    ) => void | Promise<void>;
    /**Combobox placeholder */
    placeholder?: string;
    /**Input search placehoder */
    searchPlaceholder?: string;

    /**Hiển command searchsearch */
    showCommand?: boolean;

    /**Allow clear data */
    allowClear?: boolean;

    /**
     * Làm cho các item trong CommandList luôn mounted
     * ==> Nếu này true có nghĩa là không thể filter theo keyword
     * @description Nếu bạn muốn sử dụng filter thì phải để itemForceMount là false
     * @default true
     * */
    itemForceMount?: boolean;

    /**
     * Khi đóng popover sẽ clear giá trị search
     */
    clearSearchValueWhenPopoverClose?: boolean;

    id?: string;
    className?: string;

    wrapperClassName?: string;

    /**pass props */
    popoverContentProps?: React.ComponentPropsWithoutRef<typeof PopoverContent>;

    /**Show loading in command search */
    isSearching?: boolean;
    /**Show loadmore */
    isLoadMore?: boolean;
    /**
     * Hàm search debounce đẩy keyword search ra ngoài sử dụng cho useQuery
     *
     * Debounce mặc định là 1000ms
     */
    onSearch?: (keyword: string) => void | Promise<void>;
    /**delay time ms */
    delay?: number;

    /**
     * Handle scroll trong list của input
     */
    onScrollCommandList?: (e: React.UIEvent<HTMLDivElement, UIEvent>) => void;

    /**
     * Thay thế bằng input mode
     */
    inputMode?: boolean;
    /**
     * Cusomter generate options đối với trường hợp sử dụng input mode
     * */
    genInputModeOption?: (keyword: string) => Partial<ComboboxOption<T>>;

    /**Min length mà inputMode có thể select
     * @description Độ dài tối thiệu của inputMode để có thể tạo ra option
     */
    minInputLength?: number;

    /**
     * custom render option
     */
    renderOption?: (option: any) => React.ReactNode;
    /**
     * Chỉ đọc
     */
    readOnly?: boolean;
    /**
     * Disable combobox
     */
    disabled?: boolean;
    /**
     * Đóng popover khi chọn xong
     */
    closeOnSelect?: boolean;

    container?: any;
};
export type ComboboxRef = {};

export const Combobox = React.memo(
    React.forwardRef<ComboboxRef, ComboboxProps>((props, ref) => {
        const {
            id,
            options = [],
            value,
            onChange,
            placeholder,
            searchPlaceholder,
            className,
            wrapperClassName,
            popoverContentProps,
            onSearch,
            onScrollCommandList,
            delay = 1000,
            renderOption,
            inputMode,
            genInputModeOption,
            onAfterValueChange,
            minInputLength = 4,
            showCommand = true,
            isSearching = false,
            isLoadMore = false,
            disabled = false,
            readOnly = false,
            closeOnSelect = true,
            itemForceMount = true,
            clearSearchValueWhenPopoverClose = true,
            allowClear = false,
            container,
        } = props;

        const _deboune = React.useRef<NodeJS.Timeout>();
        const _isCheckedSelectedOption = React.useRef<boolean>(false);

        const transDef = useTranslations('System');

        const [open, setOpen] = React.useState(false);
        const [selectedOption, setSelectedOption] =
            React.useState<ComboboxOption>();
        const [extendOptions, setExtendOptions] = React.useState<
            ComboboxOption[]
        >([]);
        const [inputSearchValue, setInputSearchValue] = React.useState('');
        const [isLoading, setIsLoading] = React.useState(false);

        React.useEffect(() => {
            if (_isCheckedSelectedOption.current) return;
            if (!selectedOption && value && options.length) {
                const currentOption = options.find((x) => x.value === value);
                if (currentOption) {
                    setSelectedOption(currentOption);
                    _isCheckedSelectedOption.current = true;
                }
            }
        }, [options, selectedOption, value]);

        const onFilter = (value: string, search: string) => {
            const label = options.find((x) => x.value === value)?.label;
            if (
                value.includes(search) ||
                (typeof label === 'string' &&
                    label.toLowerCase().includes(search.toLowerCase()))
            ) {
                return 1;
            }
            return 0;
        };

        const onSelectItem = (
            currentValue?: string,
            originOpt?: ComboboxOption
        ) => {
            setOpen(closeOnSelect ? false : true);
            // giữ lại option đã được chọn hỗ trợ cho render UI command
            // nếu không sẽ lỗi mất data đối với select search api
            const _val = currentValue === value ? '' : currentValue;
            const _opt = currentValue === value ? undefined : originOpt;
            setSelectedOption(_opt);
            // Lưu lại các options được chọn vào danh sách
            // setExtendOptions((st) => {
            //     const _new = [...st].filter((x) => !!x);
            //     if (_new.findIndex((x) => x?.value === _opt?.value) === -1) {
            //         _new.unshift(_opt!);
            //     }
            //     return _new;
            // });
            //Set cho input value nếu có
            onChange?.(_val ?? '');
            onAfterValueChange?.(_val ?? '', _opt);

            // Delay 300ms để đảm bảo khi chọn xong thì input sẽ clear
            // Tránh trường hợp chọn xong mà input vẫn còn nội dung
            // Delay để UI ko bị giật
            setTimeout(() => {
                setInputSearchValue('');
            }, 300);
        };

        const handleSearch = (keyword: string) => {
            setInputSearchValue(keyword);
            if (inputMode) {
                let extOption: ComboboxOption[] = [];
                if (keyword.length >= minInputLength) {
                    extOption.push({
                        label: keyword,
                        value: keyword,
                        ...(genInputModeOption?.(keyword) ?? {}),
                        _isInput: true,
                    });
                }
                setExtendOptions(extOption);
            }
            /**
             * only fire when have prop onSearch
             */
            if (typeof onSearch === 'function') {
                setIsLoading(true);
                clearTimeout(_deboune.current);
                _deboune.current = setTimeout(() => {
                    onSearch?.(keyword);
                    setIsLoading(false);
                }, delay);
            }
        };

        const onClearSelected = () => {
            onSelectItem(selectedOption?.value, selectedOption!);
        };

        const onPopverOpenChange = React.useCallback(
            (st: boolean) => {
                // Fix cái lỗi ngu học của commandInput
                if (itemForceMount && clearSearchValueWhenPopoverClose) {
                    setInputSearchValue('');
                }

                setOpen(st);
            },
            [itemForceMount, clearSearchValueWhenPopoverClose]
        );

        const renderTriggerPopover = () => {
            const isShowLoading = isLoading || isSearching || isLoadMore;
            const isShowClearBtn = !!value && allowClear;

            return (
                <Button
                    variant='outline'
                    role='combobox'
                    aria-expanded={open}
                    className={cn(
                        'w-full max-w-full justify-between gap-2 whitespace-pre-wrap px-3 text-gray-600',

                        {
                            'text-gray-500': !value,
                        },
                        className
                    )}
                    disabled={disabled}
                >
                    <p className='line-clamp-1 break-all font-medium'>
                        {value && value === selectedOption?.value
                            ? selectedOption?.label
                            : (options.find((x) => x.value === value)?.label ??
                              placeholder ??
                              `${transDef('System_Select')} ...`)}
                    </p>

                    {isShowLoading ? (
                        <Loader
                            strokeWidth={2}
                            size={18}
                            className={cn(
                                'h-4 w-4 shrink-0 animate-spin opacity-50'
                            )}
                        />
                    ) : (
                        <div className='flex items-center'>
                            {isShowClearBtn && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClearSelected();
                                    }}
                                    title='remove'
                                    type='button'
                                    className='p-1 opacity-50 transition-all hover:scale-110 hover:text-red-500 hover:opacity-100'
                                >
                                    <X size={18} />
                                </button>
                            )}
                            <ChevronDown
                                strokeWidth={2}
                                size={18}
                                className={cn(
                                    'h-4 w-4 shrink-0 opacity-50 transition-all duration-150',
                                    {
                                        'rotate-180': open,
                                    }
                                )}
                            />
                        </div>
                    )}
                </Button>
            );
        };

        const mergeOptions = options.length ? options : extendOptions;
        const isApiHandler = isSearching || isLoading;

        const _content = (
            <PopoverContent
                {...popoverContentProps}
                className={cn(
                    'max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] p-0', // default width of popover is equal to trigger width
                    popoverContentProps?.className
                )}
            >
                <Command
                    filter={onFilter}
                    shouldFilter={
                        typeof onSearch === 'function' || itemForceMount
                            ? false
                            : true
                    }
                >
                    {showCommand && (
                        <CommandInput
                            placeholder={
                                searchPlaceholder ??
                                `${transDef('System_Search')}...`
                            }
                            className='my-1 h-8'
                            value={inputSearchValue}
                            onValueChange={handleSearch}
                            tabIndex={0}
                        />
                    )}
                    <CommandList onScroll={onScrollCommandList}>
                        {isApiHandler ? (
                            <div className='flex items-center py-6'>
                                <CustomLoading.Round size='small' />
                            </div>
                        ) : mergeOptions.length > 0 ? (
                            <>
                                <CommandGroup>
                                    {mergeOptions.map((opt, idx) => (
                                        <CommandItem
                                            disabled={opt.disabled}
                                            key={`${opt.value}_${idx}`}
                                            value={opt.value}
                                            onSelect={() => {
                                                onSelectItem(opt.value, opt);
                                            }}
                                            className={cn(
                                                'cursor-pointer gap-1 py-2 disabled:cursor-none'
                                            )}
                                            forceMount={itemForceMount}
                                        >
                                            {typeof renderOption === 'function'
                                                ? renderOption(
                                                      opt._origin ?? opt
                                                  )
                                                : opt.label}
                                            <Check
                                                size={20}
                                                className={cn(
                                                    'ml-auto h-5 w-5 min-w-5',
                                                    value === opt.value
                                                        ? 'text-primary opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                        </CommandItem>
                                    ))}
                                </CommandGroup>

                                {isLoadMore && (
                                    <div className='flex items-center py-3'>
                                        <CustomLoading.Round size='small' />
                                    </div>
                                )}
                            </>
                        ) : (
                            onSearch && (
                                <CommandEmpty className='p-2'>
                                    <div className='py-4 text-center text-sm font-medium'>
                                        {inputSearchValue
                                            ? transDef('System_NoDataFound')
                                            : transDef('System_EnterToSearch')}
                                        ...
                                    </div>
                                </CommandEmpty>
                            )
                        )}

                        {!onSearch && !isApiHandler && (
                            <>
                                {inputSearchValue && (
                                    <CommandEmpty>
                                        <div className='text-sm font-medium'>
                                            {transDef('System_NoDataFound')}
                                        </div>
                                    </CommandEmpty>
                                )}
                                {options.length === 0 && !inputSearchValue && (
                                    <CommandLoading className='p-2'>
                                        <div className='py-4 text-center text-sm font-medium'>
                                            {transDef('System_NoData')}
                                        </div>
                                    </CommandLoading>
                                )}
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        );

        return (
            <div id={id} className={cn('w-full', wrapperClassName)}>
                <Popover open={open} onOpenChange={onPopverOpenChange}>
                    {readOnly ? (
                        renderTriggerPopover()
                    ) : (
                        <PopoverTrigger asChild>
                            {renderTriggerPopover()}
                        </PopoverTrigger>
                    )}

                    {/*  */}
                    {!!container ? (
                        <PopoverPortal container={container}>
                            {_content}
                        </PopoverPortal>
                    ) : (
                        _content
                    )}
                </Popover>
            </div>
        );
    })
);
Combobox.displayName = 'Combobox';
